import axios from 'axios';
import { API_BASE_URL } from '../core/env.configs';
import { getJWTToken } from '../utils/helper';
import { CompanyApi } from './companyApi';

/**
 * Data layer for dashboard report downloads.
 *
 * Two shapes of request, because the backend has two:
 *
 *  - one feature renders inside the request and comes back as a PDF blob, so
 *    it uses axios directly with responseType 'blob'. The shared CompanyApi
 *    helpers parse JSON and would corrupt the bytes.
 *  - a section or the whole dashboard is queued and collected from the reports
 *    list, so it goes through the normal JSON helpers.
 */

const BASE_URL = API_BASE_URL || 'https://staging-api.shoorah.io/';

const ok = (res) => res?.data?.meta?.code === 1;
const payload = (res) => res?.data?.data;

export const REPORT_SCOPE = { FEATURE: 'feature', PLANE: 'plane', ALL: 'all' };
export const REPORT_DELIVERY = { DOWNLOAD: 'download', EMAIL: 'email' };
export const RANGE_KIND = { DAYS: 'days', MONTH: 'month', YEAR: 'year' };

/**
 * Turn the dashboard's filter state plus the dialog's period choice into the
 * body every report endpoint accepts. One place, so the two callers cannot
 * drift apart.
 */
export const buildReportBody = ({ scope, featureKey, plane, period, filters, delivery }) => ({
  scope,
  ...(featureKey ? { featureKey } : {}),
  ...(plane ? { plane } : {}),

  rangeKind: period?.kind || RANGE_KIND.DAYS,
  days: period?.days ?? 30,
  ...(period?.month ? { month: period.month } : {}),
  ...(period?.year ? { year: period.year } : {}),

  // Inherited from the dashboard so a filtered view exports the same slice it
  // is showing. The PDF prints them on its cover.
  department: filters?.department || '',
  location: filters?.location || '',
  age: filters?.age || '',
  gender: filters?.gender === 0 || filters?.gender ? String(filters.gender) : '',
  ethinicity: filters?.ethinicity || '',

  isFansMode: String(JSON.parse(localStorage.getItem('isB2BFansMode') || 'false')),

  ...(delivery ? { delivery } : {})
});

/** Periods this company may pick, and which features have a report. */
export const fetchReportOptions = async () => {
  const res = await CompanyApi.getReportOptions();
  return ok(res) ? payload(res) : null;
};

/**
 * One feature, rendered now and saved to disk.
 *
 * Returns an error string rather than throwing, because a failure here is a
 * message in the dialog, not a crash. A JSON body coming back on a blob
 * request is the backend's validation error — it has to be read out of the
 * blob, since responseType applies to failures too.
 */
export const downloadFeatureReport = async (body) => {
  try {
    const res = await axios.post(`${BASE_URL}company/v1/reports/download`, body, {
      headers: { Authorization: getJWTToken(), deviceType: 3 },
      responseType: 'blob'
    });

    if (res?.data?.type === 'application/json') {
      const text = await res.data.text();
      return { error: JSON.parse(text)?.meta?.message || 'Report could not be generated' };
    }

    const fileName =
      res.headers?.['content-disposition']?.split('filename=')?.[1]?.replace(/"/g, '') ||
      'shoorah-report.pdf';

    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);

    return { fileName };
  } catch (error) {
    // Validation errors arrive as a blob too, so read the body before falling
    // back to a generic message.
    let message = 'Report could not be generated';
    try {
      const body_ = error?.response?.data;
      if (body_ instanceof Blob) {
        message = JSON.parse(await body_.text())?.meta?.message || message;
      }
    } catch (parseError) {
      // keep the generic message
    }
    return { error: message };
  }
};

/** Queue a section or whole-dashboard report. Resolves once it is accepted. */
export const requestReport = async (body) => {
  const res = await CompanyApi.generateReport(body);
  return ok(res) ? { job: payload(res) } : { error: res?.data?.meta?.message || 'Request failed' };
};

/** The reports list. */
export const fetchReportJobs = async (page = 1, perPage = 10) => {
  const res = await CompanyApi.getReportJobs(page, perPage);
  if (!ok(res)) return { jobs: [], total: 0 };
  return { jobs: payload(res) || [], total: res?.data?.meta?.totalRecords || 0 };
};

/** Open a finished report. The link is signed and short-lived. */
export const openReportJob = async (jobId) => {
  const res = await CompanyApi.getReportJobLink(jobId);
  if (!ok(res)) return { error: res?.data?.meta?.message || 'Report is not ready yet' };

  const { url, fileName } = payload(res) || {};
  if (!url) return { error: 'Report is not ready yet' };

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'shoorah-report.pdf';
  link.target = '_blank';
  link.rel = 'noopener';
  link.click();

  return { fileName };
};

export const deleteReportJob = async (jobId) => {
  const res = await CompanyApi.deleteReportJob(jobId);
  return ok(res);
};

/**
 * One module the report components can be handed wholesale.
 *
 * The default for every report component; api/adminReportApi.js exports the
 * same shape for the super-admin panel. Keeping them interchangeable is what
 * lets one dialog serve both.
 */
export const companyReportApi = {
  buildReportBody,
  fetchReportOptions,
  downloadFeatureReport,
  requestReport,
  fetchReportJobs,
  openReportJob,
  deleteReportJob
};
